import {
  DoorOpen,
  DoorClosed,
  UserCheck,
  UserX,
  Car,
  ShieldAlert,
  MapPin,
  Clock,
  ArrowRight,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from "lucide-react";
import {
  sperren,
  einfahrterlaubnisse,
  ereignisse,
  getKpis,
} from "../data/mockData";
import { useEffect } from "react";
import { track } from "@/lib/track";

function SmallBox({ value, label, icon: Icon, color, footer, testId }) {
  return (
    <div className={`small-box ${color}`} data-testid={testId}>
      <div className="inner">
        <h3>{value}</h3>
        <p>{label}</p>
      </div>
      <div className="al-icon">
        <Icon size={70} strokeWidth={1.5} />
      </div>
      <a className="small-box-footer" href="#detail" onClick={(e) => e.preventDefault()}>
        {footer} <ArrowRight size={13} />
      </a>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    aktiv: { cls: "badge-success", icon: CheckCircle2, text: "Aktiv" },
    ausstehend: { cls: "badge-warning", icon: AlertCircle, text: "Ausstehend" },
    abgelaufen: { cls: "badge-secondary", icon: XCircle, text: "Abgelaufen" },
  };
  const s = map[status] || map.abgelaufen;
  const Icon = s.icon;
  return (
    <span className={`al-badge ${s.cls}`}>
      <Icon size={13} /> {s.text}
    </span>
  );
}

function SperrenBadge({ status }) {
  const offen = status === "offen";
  return (
    <span className={`al-badge ${offen ? "badge-danger" : "badge-success"}`}>
      {offen ? <DoorOpen size={13} /> : <DoorClosed size={13} />} {offen ? "Offen" : "Geschlossen"}
    </span>
  );
}

function BesetzungBadge({ besetzung }) {
  const bemannt = besetzung === "bemannt";
  return (
    <span className={`al-badge ${bemannt ? "badge-info" : "badge-secondary"}`}>
      {bemannt ? <UserCheck size={13} /> : <UserX size={13} />} {bemannt ? "Bemannt" : "Unbemannt"}
    </span>
  );
}

export default function Dashboard() {
  const kpi = getKpis();
  useEffect(() => { track("view_dashboard"); }, []);

  return (
    <>
      <div className="al-content-header">
        <h1>Dashboard</h1>
        <div className="al-breadcrumb">
          <span>Start</span>
          <span>/</span>
          <span style={{ color: "#212529" }}>Sicherheitskontrollen</span>
        </div>
      </div>

      <div className="al-content" data-testid="dashboard">
        {/* KPI Kacheln */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-2">
          <SmallBox
            testId="kpi-offen"
            value={kpi.offen}
            label="Offene Sperren"
            icon={DoorOpen}
            color="bg-al-danger"
            footer="Details ansehen"
          />
          <SmallBox
            testId="kpi-geschlossen"
            value={kpi.geschlossen}
            label="Geschlossene Sperren"
            icon={DoorClosed}
            color="bg-al-success"
            footer="Details ansehen"
          />
          <SmallBox
            testId="kpi-bemannt"
            value={kpi.bemannt}
            label="Bemannte Sperren"
            icon={UserCheck}
            color="bg-al-info"
            footer="Details ansehen"
          />
          <SmallBox
            testId="kpi-unbemannt"
            value={kpi.unbemannt}
            label="Unbemannte Sperren"
            icon={UserX}
            color="bg-al-warning"
            footer="Details ansehen"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Einfahrterlaubnisse Tabelle */}
          <div className="lg:col-span-2">
            <div className="al-card">
              <div className="al-card-header">
                <Car size={18} className="title-icon" />
                Aktuelle Einfahrterlaubnisse
                <span className="al-card-tools">
                  <span className="al-badge badge-success">{kpi.aktivePermits} aktiv</span>
                </span>
              </div>
              <div className="al-card-body p-0">
                <div style={{ overflowX: "auto" }}>
                  <table className="al-table" data-testid="permits-table">
                    <thead>
                      <tr>
                        <th>Nummernschild</th>
                        <th>Fahrzeug / Firma</th>
                        <th>Sperre</th>
                        <th>Gültig bis</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {einfahrterlaubnisse.map((e) => (
                        <tr key={e.id} data-testid={`permit-row-${e.id}`}>
                          <td>
                            <span className="al-plate">{e.nummernschild}</span>
                          </td>
                          <td>
                            <div style={{ fontWeight: 600 }}>{e.fahrzeug}</div>
                            <div style={{ fontSize: "0.8rem", color: "#6c757d" }}>{e.firma}</div>
                          </td>
                          <td>
                            <span className="al-badge badge-secondary">
                              <ShieldAlert size={12} /> {e.sperre}
                            </span>
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>{e.gueltigBis}</td>
                          <td>
                            <StatusBadge status={e.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Standortübersicht + Ereignisse */}
          <div>
            <div className="al-card">
              <div className="al-card-header">
                <MapPin size={18} className="title-icon" />
                Standortübersicht
              </div>
              <div className="al-card-body">
                <div className="al-map" data-testid="site-map">
                  <div className="al-map-site">Gelände</div>
                  {sperren.map((s) => (
                    <div
                      key={s.id}
                      className="al-marker"
                      style={{ left: `${s.pos.x}%`, top: `${s.pos.y}%` }}
                      title={`${s.name} – ${s.status}`}
                      data-testid={`map-marker-${s.id}`}
                    >
                      {s.status === "offen" && <span className="pulse offen" />}
                      <span className={`dot ${s.status}`}>
                        {s.status === "offen" ? <DoorOpen size={14} /> : <DoorClosed size={14} />}
                      </span>
                      <span className="lbl">{s.id}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-4 mt-3" style={{ fontSize: "0.8rem", color: "#495057" }}>
                  <span className="flex items-center gap-1">
                    <span className="dot geschlossen" style={{ width: 12, height: 12, borderRadius: "50%", background: "#198754", display: "inline-block" }} />
                    Geschlossen
                  </span>
                  <span className="flex items-center gap-1">
                    <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#dc3545", display: "inline-block" }} />
                    Offen
                  </span>
                </div>
              </div>
            </div>

            <div className="al-card">
              <div className="al-card-header">
                <Clock size={18} className="title-icon" />
                Letzte Ereignisse
              </div>
              <div className="al-card-body">
                <ul className="al-timeline" data-testid="events-timeline">
                  {ereignisse.map((ev, i) => (
                    <li key={i}>
                      <span className="tl-time">{ev.zeit}</span>
                      <span className={`tl-dot ${ev.typ}`} />
                      <span className="tl-text">{ev.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Terrorsperren Statustabelle */}
        <div className="al-card">
          <div className="al-card-header">
            <ShieldAlert size={18} className="title-icon" />
            Terrorsperren – Status &amp; Besetzung
          </div>
          <div className="al-card-body p-0">
            <div style={{ overflowX: "auto" }}>
              <table className="al-table" data-testid="barriers-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Bezeichnung</th>
                    <th>Standort</th>
                    <th>Status</th>
                    <th>Besetzung</th>
                    <th>Letzte Änderung</th>
                  </tr>
                </thead>
                <tbody>
                  {sperren.map((s) => (
                    <tr key={s.id} data-testid={`barrier-row-${s.id}`}>
                      <td style={{ fontWeight: 700 }}>{s.id}</td>
                      <td>{s.name}</td>
                      <td>{s.standort}</td>
                      <td>
                        <SperrenBadge status={s.status} />
                      </td>
                      <td>
                        <BesetzungBadge besetzung={s.besetzung} />
                      </td>
                      <td style={{ whiteSpace: "nowrap", color: "#6c757d", fontSize: "0.85rem" }}>
                        {s.letzteAenderung}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
